import React from 'react'
import { useTranslation } from 'react-i18next'

import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import CircularProgress from '@mui/material/CircularProgress'

import Alert from '../alert/alert'
import ProfileLibraryCard from './profileLibraryCard'
import CheckboxGroup from './checkboxGroup'
import { Profile } from '../../types/profile'
import { get } from '../../utils/service'
import { ProfileMetadataFilter } from '../../types/profileMetadataFilter'

const IS_IMAGE_ENABLED = false

interface ProfileMetadataEditorProps {
  isReadonly?: boolean
  profile: Profile
  notes: string
  onNotesChanged: (newNotes: string) => void
  selectedFilters: Array<{ id: number; label: string }>
  onFiltersChanged: (value: number, isChecked: boolean) => void
  imageData: { id?: number; b64_img_data: string; b64_img_type: string } | null
  onImageDataChanged: (b64_img_data: string, b64_img_type: string) => void
}

const ProfileMetadataEditor = (props: ProfileMetadataEditorProps): JSX.Element => {
  const { t } = useTranslation()

  const [isMetadataOpen, setIsMetadataOpen] = React.useState<boolean>(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [hasLoadConfigError, setHasLoadConfigError] = React.useState<boolean>(false)
  const [filterConfig, setFilterConfig] = React.useState<Array<ProfileMetadataFilter>>([])
  const [imageError, setImageError] = React.useState<string>('')

  React.useEffect(() => {
    if (isMetadataOpen) {
      setIsLoading(true)
      get<Array<ProfileMetadataFilter>>('profiles/metadata_filters/')
        .then((response) => {
          setIsLoading(false)
          setFilterConfig(response.data)
        })
        .catch(() => {
          setIsLoading(false)
          setHasLoadConfigError(true)
        })
    }
  }, [isMetadataOpen])

  const getImageString = (): string | undefined => {
    return props.imageData ? `data:${props.imageData.b64_img_type};base64,${props.imageData.b64_img_data}` : undefined
  }

  return (
    <>
      <Button onClick={() => setIsMetadataOpen(true)}>
        {props.isReadonly ? t('profilelibrary.viewmetadata') : t('profilelibrary.editmetadata')}
      </Button>
      <Dialog fullScreen open={isMetadataOpen} onKeyDown={(e) => e.stopPropagation()}>
        <Stack sx={{ ml: 'auto', mr: 'auto', p: 4 }} spacing={4}>
          <Typography variant="h3">{t('profilelibrary.profilemetadata')}</Typography>
          <Alert severity="info">
            {!props.isReadonly && (
              <Typography sx={{ mb: 2 }} variant="body2">
                {t('profilelibrary.profilemetadataintro')}
              </Typography>
            )}
            <Typography variant="body2">{t('profilelibrary.differentroasterwarning')}</Typography>
          </Alert>

          {isLoading && <CircularProgress />}
          {filterConfig && filterConfig.length > 0 && (
            <>
              <TextField
                label={t('profilelibrary.profiledescription')}
                multiline
                defaultValue={props.notes}
                inputProps={{ maxLength: 2047 }}
                disabled={props.isReadonly}
                onBlur={(event) => props.onNotesChanged(event.target.value)}
                minRows={2}
              />
              <Stack>
                <Typography>{t('profilelibrary.profilefilters')}</Typography>
                {!props.isReadonly && (
                  <Typography variant="caption">{t('profilelibrary.profilefiltershelpttext')}</Typography>
                )}
                <Stack direction="row" flexWrap="wrap" sx={{ mt: 2 }}>
                  {filterConfig.map((x) => {
                    return (
                      <CheckboxGroup
                        key={x.id}
                        isReadonly={props.isReadonly}
                        header={x.name}
                        values={x.filter_values}
                        checkedValues={props.selectedFilters.map((y) => y.id)}
                        onChangeValue={props.onFiltersChanged}
                      />
                    )
                  })}
                </Stack>
              </Stack>
              {!props.isReadonly && (
                <>
                  {IS_IMAGE_ENABLED && (
                    <Stack spacing={1}>
                      <Typography>{t('profilelibrary.companylogo')}</Typography>
                      {!props.imageData && <Alert severity="info">{t('profilelibrary.nologoselected')}</Alert>}
                      {props.imageData && <img style={{ maxWidth: '128px' }} src={getImageString()} alt="logo" />}
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Button variant="outlined" component="label">
                          {t('profilelibrary.uploadlogo')}
                          <input
                            hidden
                            accept="image/png, image/jpg, image/jpeg"
                            type="file"
                            onChange={(event) => {
                              if (event.target.files && event.target.files?.length > 0) {
                                var reader = new FileReader()
                                reader.onload = () => {
                                  const mimeType = (reader.result as string).split(',')[0]
                                  const imgData = (reader.result as string).split(',')[1]
                                  props.onImageDataChanged(imgData, mimeType)
                                }
                                reader.onerror = () => {
                                  setImageError(t('profilelibrary.imageerror'))
                                }
                                let errorMessage = ''
                                if (
                                  ['image/png', 'image/jpeg', 'image/jpg'].indexOf(event.target.files[0].type) === -1
                                ) {
                                  errorMessage = t('profilelibrary.imageformaterror')
                                } else if (event.target.files[0].size > 49152) {
                                  // 48KB
                                  errorMessage = t('profilelibrary.imagesizeerror')
                                }
                                setImageError(errorMessage)
                                if (!errorMessage) {
                                  reader.readAsDataURL(event.target.files[0])
                                }
                              }
                            }}
                          />
                        </Button>
                        <Typography sx={{ flex: 1, maxWidth: 350 }} variant="caption">
                          {t('profilelibrary.imagehelptext')}
                        </Typography>
                      </Stack>
                      {imageError && <Alert severity="error">{imageError}</Alert>}
                    </Stack>
                  )}
                  <Stack>
                    <Typography>{t('profilelibrary.preview')}</Typography>
                    <ProfileLibraryCard
                      customerUrl=""
                      selectedMachineSlug=""
                      filterConfig={filterConfig}
                      profile={{
                        ...props.profile,
                        metadata_image: props.imageData,
                        notes: props.notes,
                        selected_filters: props.selectedFilters,
                      }}
                    />
                  </Stack>
                </>
              )}
            </>
          )}
          {hasLoadConfigError && <Alert severity="error">{t('profilelibrary.loadmetadataerror')}</Alert>}
          <Button variant="contained" sx={{ alignSelf: 'flex-start' }} onClick={() => setIsMetadataOpen(false)}>
            {t('common.back')}
          </Button>
        </Stack>
      </Dialog>
    </>
  )
}

export default ProfileMetadataEditor
